import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";
import { GoogleG } from "@/components/GoogleG";
import { SITE } from "@/lib/site";

export const Testimonials = ({ items = [] }) => {
  const [idx, setIdx] = useState(0);
  const count = items.length;

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  if (!count) return null;
  const item = items[idx];

  return (
    <div className="relative" data-testid="testimonials-carousel">
      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="bg-ink/60 backdrop-blur-xl border border-bone/10 rounded-3xl p-8 sm:p-12">
          <Quote size={40} className="text-sun mb-6" />
          <p className="text-bone/90 text-lg sm:text-xl leading-relaxed font-light mb-8" data-testid="testimonial-text">"{item.text}"</p>
          <div className="flex items-center gap-4">
            {item.avatar ? (
              <img src={item.avatar} alt={item.name} className="w-12 h-12 rounded-full object-cover border-2 border-sun" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-sun/20 border-2 border-sun flex items-center justify-center font-display font-bold text-sun text-lg">
                {(item.name || "G")[0]}
              </div>
            )}
            <div>
              <p className="text-bone font-display font-bold" data-testid="testimonial-name">{item.name}</p>
              <p className="text-bone/50 text-sm">{item.trip} · {item.date}</p>
              <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" data-testid="testimonial-google-badge"
                className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] text-bone/60 hover:text-bone transition-colors">
                <GoogleG size={13} /> Google review <BadgeCheck size={12} className="text-[#4285F4]" />
              </a>
            </div>
            <div className="ml-auto flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={14} className={i < item.rating ? "fill-sun text-sun" : "text-bone/20"} />
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center gap-3 mt-6">
        <button onClick={() => setIdx((idx - 1 + count) % count)} aria-label="Previous review" data-testid="testimonial-prev"
          className="w-10 h-10 rounded-full border border-bone/20 text-bone flex items-center justify-center hover:bg-sun hover:border-sun transition-colors">
          <ChevronLeft size={18} />
        </button>
        <button onClick={() => setIdx((idx + 1) % count)} aria-label="Next review" data-testid="testimonial-next"
          className="w-10 h-10 rounded-full border border-bone/20 text-bone flex items-center justify-center hover:bg-sun hover:border-sun transition-colors">
          <ChevronRight size={18} />
        </button>
        <div className="flex gap-1.5 ml-2">
          {items.map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-sun" : "w-2 bg-bone/20"}`} />
          ))}
        </div>
      </div>
    </div>
  );
};
